import React, { useMemo, useState, useEffect } from "react";
import { Menu, Button, Tooltip } from "antd";
import { SettingOutlined } from "@ant-design/icons";
import { items } from "../../constants/items.constant";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";
import { useSidebarVisibility } from "../../hooks/useSidebarVisibility";

export default function Sidebar({ darkTheme }: { darkTheme: boolean }) {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const { isItemVisible, getItemOrder } = useSidebarVisibility();
  const [refreshKey, setRefreshKey] = useState(0);

  const userRole = authState.user?.role as UserRole;

  // Слушаем изменения в localStorage для обновления сайдбара
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key && e.key.startsWith("sidebarVisibility_")) {
        setRefreshKey((prev) => prev + 1);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    
    // Также слушаем изменения в том же окне через кастомное событие
    const handleCustomStorageChange = () => {
      setRefreshKey((prev) => prev + 1);
    };
    
    const handleOrderChange = () => {
      setRefreshKey((prev) => prev + 1);
    };
    
    window.addEventListener("sidebarVisibilityChanged", handleCustomStorageChange);
    window.addEventListener("sidebarOrderChanged", handleOrderChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("sidebarVisibilityChanged", handleCustomStorageChange);
      window.removeEventListener("sidebarOrderChanged", handleOrderChange);
    };
  }, []);

  // Функция для фильтрации дочерних элементов с учетом видимости
  const filterChildren = (children: typeof items): typeof items => {
    return children
      .filter((child) => {
        if (!child || !child.icon) return false;
        if (!child.roles.includes(userRole)) return false;
        return isItemVisible(child.key);
      })
      .map((child) => ({
        ...child,
        children: child.children ? filterChildren(child.children) : undefined,
      }));
  };

  // Фильтрация пунктов меню по ролям и видимости с учетом порядка
  const filteredItems = useMemo(() => {
    const savedOrder = getItemOrder();
    
    let filtered = items
      .filter((item) => {
        if (!item || !item.icon) return false;
        if (
          !item.roles.includes(userRole) &&
          !(
            item.children &&
            item.children.some((child) => child.roles.includes(userRole))
          )
        ) {
          return false;
        }
        // Проверяем видимость пункта меню
        if (!isItemVisible(item.key)) return false;
        return true;
      })
      .map((item) => {
        // Если у пункта есть дочерние элементы, фильтруем их тоже
        if (item.children) {
          const filteredChildren = filterChildren(item.children);
          // Если после фильтрации не осталось дочерних элементов,
          // но пункт должен быть виден, возвращаем его без детей
          return {
            ...item,
            children: filteredChildren.length > 0 ? filteredChildren : undefined,
          };
        }
        return item;
      })
      .filter((item) => {
        // Если у пункта были дочерние элементы, но после фильтрации их не осталось,
        // и сам пункт не является кликабельным (нет прямого роута), скрываем его
        if (item.children && item.children.length === 0) {
          // Проверяем, есть ли у этого ключа прямой роут в App.tsx
          // Если нет, скрываем пункт
          return false;
        }
        return true;
      });

    // Применяем сохраненный порядок, если он есть
    if (savedOrder.length > 0) {
      const orderMap = new Map(savedOrder.map((key, index) => [key, index]));
      const orderedItems: typeof filtered = [];
      const unorderedItems: typeof filtered = [];

      filtered.forEach((item) => {
        const order = orderMap.get(item.key);
        if (order !== undefined) {
          orderedItems[order] = item;
        } else {
          unorderedItems.push(item);
        }
      });

      // Объединяем упорядоченные и неупорядоченные элементы
      const result = orderedItems.filter(Boolean).concat(unorderedItems);
      return result;
    }

    return filtered;
  }, [userRole, isItemVisible, refreshKey, getItemOrder]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Компактная кнопка настроек */}
      <div
        style={{
          padding: "8px 12px",
          borderBottom: darkTheme ? "1px solid #303030" : "1px solid #f0f0f0",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          marginTop: 80,
          minHeight: 40,
        }}
      >
        <Tooltip title="Настройки отображения меню" placement="right">
          <Button
            type="text"
            icon={<SettingOutlined />}
            onClick={() => navigate("/sidebar-settings")}
            size="small"
            style={{
              color: darkTheme ? "rgba(255, 255, 255, 0.85)" : "rgba(0, 0, 0, 0.65)",
              padding: "4px 8px",
              height: "auto",
              fontSize: "14px",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = darkTheme ? "#fff" : "#000";
              e.currentTarget.style.backgroundColor = darkTheme ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = darkTheme ? "rgba(255, 255, 255, 0.85)" : "rgba(0, 0, 0, 0.65)";
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          />
        </Tooltip>
      </div>
      
      {/* Компактное меню */}
      <Menu
        onClick={(item) => {
          navigate(item.key);
        }}
        selectedKeys={[currentPath]}
        items={filteredItems}
        theme={darkTheme ? "dark" : "light"}
        mode="inline"
        style={{
          fontSize: "13px",
          flex: 1,
          borderRight: 0,
          padding: "8px 0",
        }}
        className="compact-sidebar-menu"
      />
      
      <style>{`
        .compact-sidebar-menu .ant-menu-item {
          height: 38px !important;
          line-height: 38px !important;
          margin: 3px 8px !important;
          padding: 0 14px !important;
          border-radius: 8px !important;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
          position: relative !important;
          overflow: hidden !important;
        }
        
        .compact-sidebar-menu .ant-menu-item::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 3px;
          background: linear-gradient(180deg, #1890ff 0%, #096dd9 100%);
          transform: scaleY(0);
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .compact-sidebar-menu .ant-menu-item-icon {
          font-size: 17px !important;
          margin-right: 10px !important;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        
        .compact-sidebar-menu .ant-menu-item:hover .ant-menu-item-icon {
          transform: scale(1.1);
        }
        
        .compact-sidebar-menu .ant-menu-item-selected {
          font-weight: 600 !important;
        }
        
        .compact-sidebar-menu .ant-menu-item-selected::before {
          transform: scaleY(1);
        }
        
        .compact-sidebar-menu .ant-menu-submenu {
          margin: 3px 8px !important;
        }
        
        .compact-sidebar-menu .ant-menu-submenu-title {
          height: 38px !important;
          line-height: 38px !important;
          padding: 0 14px !important;
          border-radius: 8px !important;
          margin: 0 !important;
          font-size: 13px !important;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
          position: relative !important;
        }
        
        .compact-sidebar-menu .ant-menu-submenu-title .ant-menu-item-icon {
          font-size: 17px !important;
          margin-right: 10px !important;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        
        .compact-sidebar-menu .ant-menu-submenu-title:hover .ant-menu-item-icon {
          transform: scale(1.1);
        }
        
        .compact-sidebar-menu .ant-menu-submenu-arrow {
          font-size: 11px !important;
          right: 14px !important;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        
        .compact-sidebar-menu .ant-menu-submenu-open > .ant-menu-submenu-title .ant-menu-submenu-arrow {
          transform: rotate(180deg);
        }
        
        .compact-sidebar-menu .ant-menu-submenu-inline .ant-menu-submenu-title {
          padding-right: 36px !important;
        }
        
        .compact-sidebar-menu .ant-menu-submenu .ant-menu-item {
          padding-left: 44px !important;
          font-size: 12.5px !important;
          margin: 2px 8px !important;
        }
        
        .compact-sidebar-menu .ant-menu-submenu .ant-menu-item-icon {
          font-size: 15px !important;
        }
        
        /* Темная тема */
        .compact-sidebar-menu.ant-menu-dark .ant-menu-item:hover,
        .compact-sidebar-menu.ant-menu-dark .ant-menu-submenu-title:hover {
          background: linear-gradient(90deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%) !important;
          transform: translateX(2px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
        }
        
        .compact-sidebar-menu.ant-menu-dark .ant-menu-item-selected {
          background: linear-gradient(90deg, rgba(24, 144, 255, 0.2) 0%, rgba(24, 144, 255, 0.1) 100%) !important;
          color: #69c0ff !important;
          box-shadow: 0 2px 12px rgba(24, 144, 255, 0.3) !important;
        }
        
        .compact-sidebar-menu.ant-menu-dark .ant-menu-item-selected .ant-menu-item-icon {
          color: #69c0ff !important;
        }
        
        /* Светлая тема */
        .compact-sidebar-menu.ant-menu-light .ant-menu-item:hover,
        .compact-sidebar-menu.ant-menu-light .ant-menu-submenu-title:hover {
          background: linear-gradient(90deg, rgba(24, 144, 255, 0.08) 0%, rgba(24, 144, 255, 0.04) 100%) !important;
          transform: translateX(2px);
          box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
        }
        
        .compact-sidebar-menu.ant-menu-light .ant-menu-item-selected {
          background: linear-gradient(90deg, rgba(24, 144, 255, 0.15) 0%, rgba(24, 144, 255, 0.08) 100%) !important;
          color: #1890ff !important;
          box-shadow: 0 2px 12px rgba(24, 144, 255, 0.2) !important;
        }
        
        .compact-sidebar-menu.ant-menu-light .ant-menu-item-selected .ant-menu-item-icon {
          color: #1890ff !important;
        }
        
        /* Убираем стандартные полоски */
        .compact-sidebar-menu .ant-menu-item-selected::after,
        .compact-sidebar-menu .ant-menu-submenu-selected::after {
          display: none !important;
        }
        
        /* Плавная анимация для подменю */
        .compact-sidebar-menu .ant-menu-submenu-inner {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        
        /* Скрываем скроллбар меню */
        .compact-sidebar-menu {
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* IE and Edge */
        }
        
        .compact-sidebar-menu::-webkit-scrollbar {
          display: none; /* Chrome, Safari, Opera */
        }
      `}</style>
    </div>
  );
}
